import type { BriefSchedule } from './brief';
import type { ContentTranslations } from './translation';

/**
 * Role ladder (HIERARCHY_SPEC §2): super_admin → district_admin →
 * school_admin → coach / team_manager → athlete / parent. Every organization
 * is a school — inside a district or standalone — so its admin is a
 * school_admin either way. The role string itself carries no rule authority:
 * claims sync mints the `admin` / `org_admin` claims from
 * `organizations.admin_ids`, and `district_admin` from `districts.admin_ids`.
 */
export type UserRole = 'athlete' | 'coach' | 'school_admin' | 'super_admin' | 'player' | 'parent' | 'student' | 'team_manager' | 'district_admin';

/**
 * How an account came to exist. 'managed' accounts are owned by a district
 * (SSO + roster sync): their name/email/role come from the SIS and are not
 * user-editable. Everything predating districts is 'self_service'.
 */
export type AccountType = 'managed' | 'self_service';

export type ProvisionedBy = 'roster_sync' | 'sso_jit' | 'self' | 'admin_invite';

export type DeactivationReason =
  | 'left_roster'
  | 'district_suspended'
  | 'admin_action';

export type OrganizationType = 'organization' | 'group';

/**
 * Single source of truth for the sellable surface. Server-side validation
 * (districts.ts) and the entitlement resolver both derive from this list —
 * same pattern as ROSTERING_SOURCES, which drifted from its validator once
 * and should not again.
 */
export const ORGANIZATION_PERMISSIONS = [
  'ai_analysis',
  'advanced_analytics',
  'team_management',
  'challenge_creation',
  'video_storage',
  'custom_branding',
] as const;

export type OrganizationPermission = (typeof ORGANIZATION_PERMISSIONS)[number];

export type InvitationStatus = 'pending' | 'accepted' | 'pending_approval' | 'approved' | 'rejected' | 'expired';

export type ParentLinkStatus = 'pending' | 'active' | 'revoked';
export type LinkageState = 'unlinked' | 'pending' | 'linked' | 'suspended';

// Reaction system types
export type ReactionEmoji = '👍' | '❤️' | '👏' | '🔥' | '🎉';
export const REACTION_EMOJIS: ReactionEmoji[] = ['👍', '❤️', '👏', '🔥', '🎉'];

export interface ReactionCounts {
  '👍'?: number;
  '❤️'?: number;
  '👏'?: number;
  '🔥'?: number;
  '🎉'?: number;
}

/**
 * Represents a parent-child relationship link
 * Stored at /clients/{clientKey}/parent_links/{linkId}
 */
export interface ParentLink {
  id: string;
  parent_id: string;           // Parent's user ID
  child_id: string;            // Athlete's user ID
  status: ParentLinkStatus;
  invite_code: string;         // Code used to establish the link
  // Denormalized data for easier querying
  parent_email?: string;
  parent_name?: string;
  child_name?: string;
  child_team_ids?: string[];   // Teams the child is on (for parent document uploads)
  // Timestamps
  created_at: Date;
  linked_at?: Date;            // When status changed to 'active'
  revoked_at?: Date;           // When status changed to 'revoked'
  revoked_by?: string;         // Who revoked the link (parent_id, child_id, or admin)
  // Notification preferences for this link
  notification_preferences?: {
    session_completed: boolean;
    challenge_completed: boolean;
    coach_messages: boolean;
    team_announcements: boolean;
  };
  // How the link came to exist: invite codes (self-service families) or the
  // district roster's guardian edges (no codes; server-created)
  source?: 'invite_code' | 'roster';
  district_id?: string;
}

/**
 * The public schedule page (AUTOMATION_SPEC A10). Server-owned: written only
 * by `setPublicSchedule`, and rules deny the key to clients. The slug is
 * minted on the first enable and kept when the page is switched off, so
 * re-enabling gives back the link the school already printed.
 */
export interface PublicScheduleState {
  enabled: boolean;
  slug: string;
  url: string;
  updated_at?: Date | { toDate: () => Date } | null;
  updated_by?: string;
}

export interface Organization {
  id: string;
  name: string;
  type: OrganizationType;
  description?: string;
  logo_url?: string;
  admin_ids: string[]; // Array of organization admin user IDs
  /**
   * Set/cleared only by upsertDistrict when this org is a school in a
   * district (HIERARCHY_SPEC §3); client writes are blocked in rules.
   */
  district_id?: string | null;
  created_at: Date;
  updated_at: Date;
  is_active: boolean;
  // Organization-specific fields
  billing: {
    /**
     * 'none' = this org does not pay for itself; it is either covered by its
     * district (BILLING_SPEC §5.3) or unlicensed. The two are distinguished by
     * resolution, not by a second field.
     */
    plan_type: 'none' | 'basic' | 'premium';
    stripe_customer_id?: string;
    stripe_subscription_id?: string;
    billing_email?: string;
    next_billing_date?: Date;
    status: 'pending' | 'active' | 'cancelled' | 'canceled' | 'past_due' | 'trialing'; // pending = hasn't paid yet, cancelled/canceled for compatibility
    // Additional billing fields
    payment_type?: 'subscription' | 'one_time' | 'invoice'; // How they pay
    billing_cycle?: 'monthly' | 'yearly'; // Billing frequency
    price_cents?: number; // Price in cents
  };
  permissions: OrganizationPermission[]; // Permissions enabled for this org
  settings: {
    allow_public_teams: boolean;
    require_admin_approval: boolean;
    max_members?: number;
    custom_domain?: string;
  };
  invite_code?: string; // For easy joining
  metadata?: {
    industry?: string;
    location?: string;
    website?: string;
    contact_email?: string;
  };
  // Custom branding (premium feature)
  branding?: {
    primary_color?: string; // Hex color code (e.g., '#00F0FF')
    secondary_color?: string; // Hex color code (e.g., '#FF0080')
    logo_url?: string; // Organization logo URL
    logo_position?: { x: number; y: number }; // Logo position offset
    logo_zoom?: number; // Logo zoom level (1 = 100%)
  };
  // Athleto representative assigned to this organization
  athleto_rep_id?: string; // User ID of the assigned Athleto rep
  // Message monitoring settings
  monitoring?: {
    lastAnalyzedAt?: Date; // Last time messages were analyzed by GPT
  };
  /** Public schedule page (AUTOMATION_SPEC A10); written only by setPublicSchedule. */
  public_schedule?: PublicScheduleState;
  /**
   * The "human API" (AUTOMATION_SPEC §5 T1): the people a bus request or a
   * facility hold goes to. Editable by org admins; the event cascades read
   * them. `timezone` (IANA) is the school's one zone — request emails render
   * times in it, and the morning brief is scheduled by it. There is no second
   * timezone field anywhere; the Morning brief card edits this one.
   */
  operations?: {
    transportation?: { name?: string; email: string };
    facilities?: { name?: string; email: string };
    timezone?: string;
  };
  /**
   * When the morning brief is generated and whether it is also mailed
   * (DEPARTMENT_BRIEF_SPEC §6, NEXT_QUEUE_SPEC §4.2). Absent means the
   * default slot, no email. Editable by org admins — `scheduled_at` and
   * `emailed_at` live on the brief document, which no client can write, so
   * nothing here is server-owned.
   */
  brief?: BriefSchedule;
}

export interface OrganizationMembership {
  id: string;
  organization_id: string;
  user_id: string;
  role: 'member' | 'admin'; // Role within the organization
  joined_at: Date;
  permissions: OrganizationPermission[]; // User's enabled permissions within org
  status: 'active' | 'suspended' | 'pending_approval';
  invited_by?: string;
  approved_by?: string;
  approved_at?: Date;
}

export interface OrganizationInvitation {
  id: string;
  organization_id: string;
  invited_user_id?: string; // If inviting specific user
  invited_email?: string; // If inviting by email
  invited_by: string;
  invite_code: string;
  status: InvitationStatus;
  expires_at: Date;
  created_at: Date;
  accepted_at?: Date; // When user accepted the invitation
  approved_by?: string;
  approved_at?: Date;
  rejected_by?: string;
  rejected_at?: Date;
  rejection_reason?: string;
}

export interface RecruiterVerificationRequest {
  id: string;
  user_id: string;
  user_email: string;
  user_display_name: string;
  university: string;
  position: string;
  recruiting_regions: string[];
  sports_focus: string[];
  university_email: string; // Must be from university domain
  phone_number?: string;
  additional_info?: string;
  proof_documents?: string[]; // URLs to uploaded documents (ID, employment verification, etc.)
  status: 'pending' | 'approved' | 'rejected';
  created_at: Date;
  updated_at: Date;
  reviewed_by?: string; // Super admin who reviewed
  reviewed_at?: Date;
  rejection_reason?: string;
}

export interface Achievement {
  id: string;
  title: string;
  description?: string;
  type: 'championship' | 'tournament' | 'award' | 'milestone' | 'other';
  level?: 'state' | 'regional' | 'national' | 'international';
  date?: Date;
  organization?: string;
  image_url?: string;
}

export interface UserProfile {
  uid: string;
  email: string | null;
  display_name: string | null;
  photo_url: string | null;
  photo_position?: { x: number; y: number }; // Position for profile photo cropping
  photo_zoom?: number; // Zoom level for profile photo (1.0 = 100%, 2.0 = 200%, etc.)
  cover_photo_url?: string | null;
  cover_photo_position?: { x: number; y: number }; // Position for cover photo cropping
  cover_photo_zoom?: number; // Zoom level for cover photo (1.0 = 100%, 2.0 = 200%, etc.)
  role: UserRole;
  created_at: Date;
  updated_at: Date;
  // Organization membership
  organization_id?: string; // Primary organization
  organization_memberships?: string[]; // All organization memberships
  enabled_permissions?: OrganizationPermission[]; // Permissions enabled by org admin
  // Team memberships
  team_ids?: string[]; // All teams the user is part of
  // Access linkage states
  org_link_state?: LinkageState;
  team_link_state?: LinkageState;
  access_state_updated_at?: Date;
  access_state_reason?: string;
  pending_org_request_id?: string;
  pending_team_request_id?: string;
  pending_org_id?: string;
  pending_team_id?: string;
  pending_scope_updated_at?: Date;
  // Group memberships
  group_ids?: string[]; // All groups the user is part of
  metadata?: {
    sport?: string; // Deprecated - use sports instead
    sports?: string[]; // Multiple sports support
    team?: string;
    location?: string;
    bio?: string;
    featured_coins?: string[]; // Array of coin IDs to display on profile
    // Athlete-specific fields
    club_teams?: string[]; // Names of club teams
    graduation_year?: number;
    height?: string;
    weight?: string;
    position?: string;
    // Coach-specific fields
    coaching_experience?: string;
    specialization?: string;
    certifications?: string[];
    // Recruiter-specific fields
    university?: string;
    recruiting_regions?: string[];
    sports_focus?: string[];
  };
  // Achievements (primarily for athletes)
  achievements?: Achievement[];
  // Recruiter verification
  is_verified_recruiter?: boolean;
  verified_at?: Date;
  verification_badge?: {
    verified: boolean;
    verified_by?: string; // Super admin who verified
    verified_at?: Date;
    university?: string;
    position?: string;
  };
  // Onboarding
  has_completed_onboarding?: boolean;
  // Push notification tokens
  fcm_tokens?: string[]; // Firebase Cloud Messaging tokens for push notifications
  notification_settings?: {
    enabled: boolean;
    message_notifications: boolean;
    mention_notifications: boolean;
    channel_notifications: boolean;
    announcement_notifications?: boolean;
    email_digests?: boolean; // Daily email digest of unread announcements
  };
  // Content translation (docs/TRANSLATION_SPEC.md §4.2)
  preferred_language?: string; // BCP-47 tag, e.g. 'es', 'zh', 'pt-BR'. Unset = 'en'
  auto_translate?: boolean;    // Render foreign-language content translated by default
  // Mirroring fields
  currently_mirroring_uid?: string;
  is_mirroring?: boolean;
  // Parent-child linking
  linked_parent_id?: string;           // For athletes: first linked parent (legacy single slot; kept one release, see linked_parent_ids)
  linked_parent_ids?: string[];        // For athletes: every linked parent/guardian (ROSTERING_COMPLETION_SPEC G8)
  linked_children_ids?: string[];      // For parents: array of linked children's user IDs
  parent_invite_code?: string;         // For athletes: code to share with parent
  parent_invite_code_expires_at?: Date; // Expiration for the invite code
  // District identity & rostering (SSO_SPEC §5.4).
  // Server-owned: these are written by roster sync / SSO blocking functions,
  // never by the client.
  account_type?: AccountType;          // Absent is treated as 'self_service'
  district_id?: string;
  sis_sourced_id?: string;             // Links back to roster_users
  school_org_ids?: string[];           // Schools from the roster
  managed_fields_locked?: boolean;     // UI hint: name/email/role not editable
  provisioned_by?: ProvisionedBy;
  deactivated?: boolean;               // Set by sync when a user leaves the roster
  deactivated_reason?: DeactivationReason;
}

export interface Session {
  id: string;
  user_id: string;
  sport: string;
  timestamp: Date;
  duration: number; // in seconds
  video_url?: string;
  thumbnail_url?: string;
  metrics: Record<string, any>;
  is_public: boolean;
  notes?: string;
  likes?: number;
  like_count?: number;
  comment_count?: number;
  ai_analysis?: any;
}

export interface TeamPost {
  id: string;
  team_id: string;
  user_id: string;
  user_display_name?: string;
  user_photo_url?: string;
  // Aliases for compatibility with different naming conventions
  author_name?: string; // Alias for user_display_name
  author_photo?: string; // Alias for user_photo_url
  content: string;
  type: 'post' | 'challenge_completion' | 'announcement';
  challenge_id?: string; // For challenge completion posts
  challenge_name?: string;
  challenge_value?: number;
  challenge_unit?: string;
  challenge_target_value?: number;
  media_url?: string;
  video_url?: string;
  created_at: Date;
  likes: string[]; // User IDs who liked (legacy)
  comments: Comment[]; // Legacy - comments removed in favor of reactions
  // Reaction system (replaces likes)
  reaction_counts?: ReactionCounts;
  reaction_count_total?: number;
  // Announcement/feed extensions
  title?: string; // Optional headline (announcements)
  attachments?: PostAttachment[]; // Downloadable files
  is_pinned?: boolean; // Pinned to top of feed (coach/manager only)
  comment_count?: number;
  updated_at?: Date;
  // Read receipts (announcements)
  read_by?: string[];
  read_count?: number;
  // Denormalized team roles so security rules can allow coach/manager moderation
  team_coach_ids?: string[];
  team_manager_ids?: string[];
  // Machine translation — server-written only, forgery-guarded in rules
  // (docs/TRANSLATION_SPEC.md §4.5, §8)
  detected_lang?: string;
  translations?: ContentTranslations;
}

export interface PostAttachment {
  id: string;
  name: string; // Original filename shown to users
  url: string; // Download URL
  content_type: string;
  size: number; // Bytes
  storage_path: string; // Full Storage path for cleanup on delete
}

export interface TeamPostComment {
  id: string;
  post_id: string;
  team_id: string;
  user_id: string;
  user_display_name?: string;
  user_photo_url?: string;
  content: string;
  created_at: Date;
  // Denormalized team roles so security rules can allow coach/manager moderation
  team_coach_ids?: string[];
  team_manager_ids?: string[];
}

/**
 * Default roster seeded at team creation and lazily backfilled onto legacy
 * teams (ROSTER_MODEL_SPEC R0). The id is fixed — not generated — so the
 * client seed, the lazy backfill, and server-side directory adds all converge
 * on the same roster without coordination. An entry with no `squads` tags
 * is treated as belonging to this roster.
 */
export const DEFAULT_ROSTER_ID = 'main';

export const DEFAULT_ROSTER = {
  id: DEFAULT_ROSTER_ID,
  name: 'Roster',
  color: '#000000',
};

export interface Team {
  id: string;
  name: string;
  sport: string;
  description?: string;
  coach_id: string; // Legacy field - keeping for backward compatibility
  coach_ids: string[]; // Array of coach user IDs (supports multiple coaches)
  manager_ids?: string[]; // Array of team manager user IDs (can manage team but not full coach privileges)
  athlete_ids?: string[]; // Array of athlete user IDs
  members: string[]; // User IDs
  created_at: Date;
  updated_at: Date;
  invite_code?: string; // Code for athletes to join the team
  org_link_code?: string; // Code for linking team to an organization
  is_private: boolean;
  photo_url?: string;
  primary_color?: string;
  secondary_color?: string;
  cover_photo_url?: string | null; // Team cover photo
  cover_photo_position?: { x: number; y: number }; // Position for cover photo cropping
  cover_photo_zoom?: number; // Zoom level for cover photo (1.0 = 100%, 2.0 = 200%, etc.)
  challenges?: string[]; // Challenge IDs
  location?: string;
  // Organization integration
  organization_id?: string; // If team belongs to an organization
  scope: 'public' | 'organization' | 'group'; // Team visibility scope
  /**
   * Set when the roster comes from the district rather than from a coach
   * (TEAM_SOURCING_SPEC). Server-owned: rules deny the key to clients.
   * Sourced entries (`roster_entries.source === 'sis_activity'`) are
   * replaced on every import; entries a coach adds by hand are kept.
   */
  sourced_from?: {
    district_id: string;
    key: string;
    /**
     * Which delivery fills this roster: the athletic office's team file, an
     * SIS class period a per-district rule named as a team (§3 phase 3), or an
     * athletic registration platform an adapter pulls (§5 phase 4).
     * Absent on teams written before the field existed — those are team-file
     * teams, and every reader defaults to 'team_file'.
     */
    source?: 'team_file' | 'class' | 'platform';
    /**
     * The platform, when `source` is 'platform' — a `TeamSourceVendor`
     * ('aktivate', 'arbiter', …). The District Teams chip reads it as
     * "Platform · Aktivate".
     */
    vendor?: string | null;
    season?: string | null;
    last_synced_at?: Date | { toDate: () => Date } | null;
    last_count?: number;
  } | null;
  /** Public schedule page (AUTOMATION_SPEC A10); written only by setPublicSchedule. */
  public_schedule?: PublicScheduleState;
  /** Subscribed district schedule feed (AUTOMATION_SPEC §3.4); written only by setScheduleFeed. */
  schedule_feed?: {
    url: string;
    enabled: boolean;
    timezone?: string;
    last_synced_at?: Date | { toDate: () => Date } | null;
    last_error?: string | null;
    last_count?: number;
  };
  settings?: {
    rostersEnabled?: boolean;
    playbookEnabled?: boolean;
    documentsEnabled?: boolean;
    trainingLibraryEnabled?: boolean;
    lineupsEnabled?: boolean; // All sports - starting lineups & depth charts
    baserunningEnabled?: boolean; // Baseball/softball only
    defensiveAlignmentsEnabled?: boolean; // Baseball/softball only
    // Parent messaging settings
    allowParentCoachMessaging?: boolean; // If true, parents can initiate DMs with coaches
  };
  // Roster system
  rosters?: Array<{
    id: string;
    name: string;
    color: string;
  }>;
  // Squads, jersey numbers and positions live on roster_entries (ROSTER_MODEL_SPEC
  // R1; the team-doc maps were retired in ROSTERING_COMPLETION_SPEC G7).
  // Team positions (coach-customizable)
  positions?: TeamPosition[]; // Custom positions for this team (falls back to sport defaults if not set)
  cover_photo_bg_color?: string; // Background color when image doesn't fill container (hex code)
  // Sport-specific ladder settings
  golfSettings?: {
    varsityCount: number;
  };
  tennisSettings?: {
    singlesCount: number;
    doublesCount: number;
  };
  // Parent tracking
  parent_ids?: string[];  // Array of parent user IDs linked to athletes on this team
  stats?: {
    total_sessions: number;
    average_score: number;
    active_challenges: number;
  };
}

// ============================================================================
// ROSTER ENTRIES (ROSTER_MODEL_SPEC R1)
// ============================================================================

export type RosterEntryRole = 'athlete' | 'coach' | 'manager';

export type RosterEntryStatus = 'active' | 'pending_signin' | 'inactive';

/** How the entry came to exist on this team's roster. */
export type RosterEntrySource = 'invite' | 'directory' | 'manual' | 'sis_activity';

/**
 * Live athletic eligibility, written onto the roster entry by the server's
 * eligibility job (AUTOMATION_SPEC A1) and never by clients (rules deny the
 * key). Derived from the team's document_requirements + document_uploads
 * with the same helpers the dashboards use (common/src/utils/clearance).
 * Absent on teams that define no required upload-type document — unless an
 * academic hold applies (A11), which is eligibility with no documents in it.
 *
 * `ineligible` is academic (AUTOMATION_SPEC A11) and outranks every document
 * state, the athletic office's attestation included: the state association's
 * grade rule is not something a form can satisfy. It is deliberately NOT a
 * `ClearanceStatus` — that union is the range of the pure document
 * derivation, which can never produce it.
 */
export interface RosterEligibility {
  status: 'cleared' | 'incomplete' | 'expiring_soon' | 'expired' | 'ineligible';
  /** Required items missing an upload or a required signature. */
  missing: number;
  expired: number;
  expiring_soon: number;
  total_required: number;
  computed_at: Date;
  /** Status the last nudge went out for, and when — the re-nudge policy reads these. */
  nudged_status?: RosterEligibility['status'] | null;
  nudged_at?: Date | null;
  /**
   * The athletic office cleared this athlete on the team file
   * (TEAM_SOURCING_SPEC §3, phase 2) rather than the uploads adding up. The
   * status is `cleared` with nothing missing, so A1 chases only what the
   * office has not collected.
   */
  attested?: boolean;
  /**
   * Why the academic policy says ineligible ("GPA 1.80 is below 2.00"), copied
   * onto the flag so every surface that already reads `eligibility` — the
   * roster chip, the lineup, the parent home — can say why without a second
   * read. Only set when `status` is `ineligible`.
   */
  academic_reasons?: string[];
}

/**
 * Academic standing from the district's grade export (AUTOMATION_SPEC A11),
 * written onto the roster entry by `importGrades` — server-owned, rules deny
 * the key like `eligibility` and `office_clearance`.
 *
 * The verdict carries its own window: the policy's `effective_from`/
 * `effective_to` are copied here at import so A1 and game day can ask "is
 * this hold in force at kickoff" without reading the district document.
 */
export interface AcademicStanding {
  /** False = academically ineligible while the window holds. */
  eligible: boolean;
  /** On the district's scale; null when the file carried no GPA. */
  gpa: number | null;
  /** Failing grades; null when the file carried no count. */
  f_count: number | null;
  /** The grading period the file named, e.g. "2026 Q1". */
  term: string | null;
  /** The date the grades are as of, when the file dates them. */
  as_of: Date | null;
  /** Why, when not eligible; also carries "no GPA in the file" for an unknown. */
  reasons: string[];
  /** True when the policy needs a metric the file did not carry — not a hold. */
  unknown?: boolean;
  evaluated_at: Date;
  source: 'grades_file' | 'oneroster';
  /** The sync run that carried the file. */
  sync_id?: string | null;
  /** Copied from the policy: the verdict applies only inside this window. */
  effective_from?: Date | null;
  effective_to?: Date | null;
}

/**
 * The athletic office's clearance decision, carried on the district's team
 * file (TEAM_SOURCING_SPEC §3) and written onto the roster entry by the
 * import — server-owned, rules deny the key to clients. It is the office's
 * word, not a document: the app never holds the physical, only the date it
 * lapses, if the file gives one.
 */
export interface OfficeClearance {
  cleared: boolean;
  physical_on_file: boolean;
  /** Null when the file says a physical is on file but not when it expires. */
  physical_expires_at: Date | null;
  attested_at: Date;
  /**
   * Which delivery carried the office's word: its own export, or the
   * registration platform the office keeps it in (TEAM_SOURCING_SPEC §5
   * phase 4). A class period never attests, so 'class' is not a value here.
   */
  source: 'team_file' | 'platform';
  /** The sync run that carried the attestation. */
  sync_id?: string | null;
}

/**
 * One roster entry per person on the team, account or not.
 * Path: clients/{clientKey}/teams/{teamId}/roster_entries/{entryId}
 *
 * A pending athlete is a first-class row: `uid: null`, `status:
 * 'pending_signin'`, real name cached from the SIS. First sign-in fills in
 * `uid` and promotes the status — the row itself never moves.
 *
 * The team document keeps only the denormalized uid arrays that security
 * rules need (`members`, `athlete_ids`, `coach_ids`, `manager_ids`),
 * maintained by the `onRosterEntryWrite` trigger — never by clients.
 */
export interface RosterEntry {
  id: string;
  team_id: string;

  // Identity — exactly one of these is authoritative at a time
  uid: string | null;              // set once the person has an account
  district_id?: string;            // set when sourced from a district roster
  sis_sourced_id?: string;         // joins to districts/{d}/roster_users/{id}

  // Cached display identity so a pending athlete is a real row in the UI
  display_name: string;
  grade?: string | null;

  role: RosterEntryRole;
  status: RosterEntryStatus;
  source: RosterEntrySource;

  // Roster attributes — one home, one granularity
  jersey_number?: string | null;
  positions: string[];
  squads: string[];                // roster-label ids, e.g. ['varsity']

  added_at: Date;
  added_by: string;
  updated_at: Date;

  /** Server-owned (AUTOMATION_SPEC A1); see RosterEligibility. */
  eligibility?: RosterEligibility;
  /** Server-owned (TEAM_SOURCING_SPEC §3); see OfficeClearance. */
  office_clearance?: OfficeClearance;
  /** Server-owned (AUTOMATION_SPEC A11); see AcademicStanding. */
  academic?: AcademicStanding;
}

/**
 * Deterministic entry ids keep the backfill idempotent and let the
 * sign-in claim path address an entry directly instead of querying.
 * An entry keeps the id it was created with; claiming only fills in `uid`.
 */
export const rosterEntryIdForUid = (uid: string): string => uid;

export const rosterEntryIdForSis = (districtId: string, sourcedId: string): string =>
  `${districtId}:${sourcedId.replace(/\//g, '_')}`;

export interface TeamJoinRequest {
  id: string;
  team_id: string;
  user_id: string;
  user_display_name: string;
  user_photo_url?: string;
  status: 'pending' | 'approved' | 'rejected';
  created_at: Date;
  reviewed_at?: Date;
  reviewed_by?: string; // Coach/manager who approved/rejected
}

export interface TeamDocument {
  id: string;
  team_id: string;
  name: string;
  file_url: string;
  file_type: string;
  file_size: number;
  uploaded_by: string;
  uploaded_by_name: string;
  uploaded_at: Date;
  description?: string;
}

// Team Position Types
export type PositionCategory = 'offense' | 'defense' | 'both' | 'special';

/**
 * Team-specific position configuration
 * Coaches can customize these per team
 */
export interface TeamPosition {
  id: string;              // Unique identifier (e.g., 'PG', 'QB', 'P')
  label: string;           // Full name (e.g., 'Point Guard', 'Quarterback')
  abbreviation: string;    // Short form (e.g., 'PG', 'QB')
  category: PositionCategory; // For sports with offense/defense split
  order: number;           // Display order
  x?: number;              // Optional field position for playbook (0-100)
  y?: number;              // Optional field position for playbook (0-100)
}

export interface VideoCategory {
  id: string;
  team_id: string;
  name: string;
  description?: string;
  parent_category_id?: string; // For subcategories
  order: number; // For custom ordering
  created_by: string;
  created_at: Date;
  updated_at: Date;
  video_count?: number; // Computed field
}

export interface TeamVideo {
  id: string;
  team_id: string;
  category_id: string; // Reference to category or subcategory
  title: string;
  description?: string;
  youtube_url: string;
  youtube_video_id: string; // Extracted from URL
  thumbnail_url?: string; // YouTube thumbnail
  is_short?: boolean; // True if this is a YouTube Short
  duration?: string; // Video duration (e.g., "5:23")
  posted_by: string;
  posted_by_name: string;
  posted_by_photo_url?: string;
  posted_at: Date;
  tags?: string[]; // Optional tags for additional filtering
  views?: number; // Track views within the app
  likes?: string[]; // User IDs who liked
}

export interface Follow {
  id: string;
  follower_id: string;
  following_id: string;
  created_at: Date;
}

export interface Comment {
  id: string;
  session_id: string;
  user_id: string;
  content: string;
  created_at: Date;
  updated_at: Date;
  likes?: number;
  parent_id?: string; // For nested comments
}

export interface Like {
  id: string;
  user_id: string;
  target_id: string; // Can be session ID or comment ID
  target_type: 'session' | 'comment';
  created_at: Date;
}

export interface Notification {
  id: string;
  user_id: string;
  type: 'like' | 'comment' | 'follow' | 'team_invite' | 'challenge' | 'mention' | 'message' | 'channel_message' | 'announcement' | 'parent_link_request' | 'parent_linked' | 'child_session' | 'child_challenge'; // V1: removed connection_request/accepted
  title: string;
  message: string;
  read: boolean;
  created_at: Date;
  related_id?: string; // ID of the related entity (session, user, conversation, etc.)
  related_type?: string;
  // For message notifications
  conversation_id?: string;
  sender_id?: string;
  sender_name?: string;
  sender_photo_url?: string;
  // For parent notifications
  child_id?: string;
  child_name?: string;
  // For announcement notifications
  team_id?: string;
  organization_id?: string;
}

export type CoinColor = 'gold' | 'silver' | 'bronze' | 'black' | 'white';
export type CoinEffect = 'fire' | 'sparkle' | 'glow' | 'none';
export type CoinRarity = 'common' | 'rare' | 'epic' | 'legendary';

export interface CoinReward {
  front_image_url: string;
  back_image_url: string;
  name: string;
  description?: string;
  color?: CoinColor;
  effect?: CoinEffect;
  rarity?: CoinRarity;
}

export type ChallengeType = 'skill' | 'fitness' | 'competition' | 'team' | 'individual' | 'other';

export interface Challenge {
  id: string;
  name: string;
  description: string;
  sport: string;
  type: ChallengeType; // Type of challenge
  target_value?: number; // Target value for the challenge (e.g., 100 shots made)
  unit?: string; // Unit for the target value (e.g., 'shots', 'yards', 'seconds')
  deadline: Date;
  start_date?: Date;
  end_date?: Date;
  created_by: string; // Coach/Admin user ID
  creator_id?: string; // Alias for created_by
  created_at: Date;
  updated_at: Date;
  is_public: boolean;
  participants: string[]; // User IDs
  completions: ChallengeCompletion[];
  rules?: string;
  max_participants?: number;
  team_id?: string; // For team-specific challenges
  group_id?: string; // For group-specific challenges
  reward?: string;
  coin_reward?: CoinReward; // Coin reward for completing the challenge
  // Organization integration
  organization_id?: string; // If challenge belongs to an organization
  scope: 'organization' | 'team'; // Challenge visibility scope (V1: removed public/group)
  required_permissions?: OrganizationPermission[]; // Permissions needed to participate
}

export interface ChallengeCompletion {
  id: string;
  challenge_id: string;
  user_id: string;
  completed_at: Date;
  value: number;
  notes?: string;
  video_url?: string;
  verified?: boolean;
  session_id?: string; // Link to session if applicable
  coin_earned?: boolean; // Whether the user earned the coin reward
}

export interface UserCoin {
  id: string;
  user_id: string;
  challenge_id: string;
  coin_reward: CoinReward;
  earned_at: Date;
  challenge_name: string;
  // Source tracking - where the coin came from
  source_type?: 'public' | 'group' | 'team' | 'organization';
  source_id?: string; // group_id, team_id, or organization_id
  source_name?: string; // Display name of the source
}

// V1: Groups removed - cross-org social features disabled for school safety

export interface RolePermissions {
  can_access_analysis: boolean;
  can_access_challenges: boolean;
  can_access_feed: boolean;
  can_access_admin: boolean;
  can_access_team_management: boolean;
  can_view_all_profiles: boolean;
  can_manage_team: boolean;
  can_create_challenges: boolean;
  // Organization management permissions
  can_manage_organizations: boolean;
  can_manage_organization_members: boolean;
  can_set_organization_permissions: boolean;
}

export interface UserPermissions {
  // Base role permissions
  base_permissions: RolePermissions;
  // Organization-specific permissions (enabled by org admin)
  organization_permissions: OrganizationPermission[];
  // Computed effective permissions
  effective_permissions: {
    can_access_ai_analysis: boolean;
    can_access_advanced_analytics: boolean;
    can_access_analysis: boolean;
    can_access_challenges: boolean;
    can_access_feed: boolean;
    can_access_admin: boolean;
    can_access_team_management: boolean;
    can_view_all_profiles: boolean;
    can_manage_team: boolean;
    can_create_challenges: boolean;
    can_manage_organizations: boolean;
    can_manage_organization_members: boolean;
    can_set_organization_permissions: boolean;
  };
}

export const ROLE_PERMISSIONS: Record<UserRole, RolePermissions> = {
  athlete: {
    can_access_analysis: false, // Requires ai_analysis permission from org
    can_access_challenges: true,
    can_access_feed: true,
    can_access_admin: false,
    can_access_team_management: false,
    can_view_all_profiles: false,
    can_manage_team: false,
    can_create_challenges: false,
    can_manage_organizations: false,
    can_manage_organization_members: false,
    can_set_organization_permissions: false,
  },
  player: {
    can_access_analysis: false, // Requires ai_analysis permission from org
    can_access_challenges: true,
    can_access_feed: true,
    can_access_admin: false,
    can_access_team_management: false,
    can_view_all_profiles: false,
    can_manage_team: false,
    can_create_challenges: false,
    can_manage_organizations: false,
    can_manage_organization_members: false,
    can_set_organization_permissions: false,
  },
  student: {
    can_access_analysis: false, // Requires ai_analysis permission from org
    can_access_challenges: true,
    can_access_feed: true,
    can_access_admin: false,
    can_access_team_management: false,
    can_view_all_profiles: false,
    can_manage_team: false,
    can_create_challenges: false,
    can_manage_organizations: false,
    can_manage_organization_members: false,
    can_set_organization_permissions: false,
  },
  coach: {
    can_access_analysis: false, // Requires ai_analysis permission from org
    can_access_challenges: true,
    can_access_feed: true,
    can_access_admin: false,
    can_access_team_management: true,
    can_view_all_profiles: false,
    can_manage_team: true,
    can_create_challenges: true,
    can_manage_organizations: false,
    can_manage_organization_members: false,
    can_set_organization_permissions: false,
  },
  parent: {
    can_access_analysis: false,
    can_access_challenges: false,
    can_access_feed: true,
    can_access_admin: false,
    can_access_team_management: false,
    can_view_all_profiles: false,
    can_manage_team: false,
    can_create_challenges: false,
    can_manage_organizations: false,
    can_manage_organization_members: false,
    can_set_organization_permissions: false,
  },
  team_manager: {
    can_access_analysis: false, // Requires ai_analysis permission from org
    can_access_challenges: true,
    can_access_feed: true,
    can_access_admin: false,
    can_access_team_management: true,
    can_view_all_profiles: false,
    can_manage_team: true,
    can_create_challenges: true,
    can_manage_organizations: false,
    can_manage_organization_members: false,
    can_set_organization_permissions: false,
  },
  // V1: recruiter role removed for school safety
  // School admin: runs one school's program (teams, rosters, announcements,
  // and billing when the school is standalone).
  school_admin: {
    can_access_analysis: false, // Requires ai_analysis permission from org
    can_access_challenges: true,
    can_access_feed: true,
    can_access_admin: false, // No platform admin access - school admin only
    can_access_team_management: true,
    can_view_all_profiles: false, // Can only view profiles within their school
    can_manage_team: true, // Can manage teams within their school
    can_create_challenges: true,
    can_manage_organizations: false, // Cannot create/delete orgs (super_admin only)
    // HIERARCHY_SPEC §2: school-scoped power is the server-minted org_admin[]
    // claim, never the role string — `school_admin` is a self-assignable
    // profile label (it is not in the server's PRIVILEGED_ROLES), so granting
    // these here would hand the org-member console to anyone who typed it.
    // composeRolePermissions() layers them back on from the claim.
    can_manage_organization_members: false,
    can_set_organization_permissions: false,
  },
  // District IT / athletic director: administers every school in their
  // district, but has no platform-wide access. Granted only by roster sync or
  // a super admin — never self-selected at onboarding.
  district_admin: {
    can_access_analysis: false,
    can_access_challenges: true,
    can_access_feed: true,
    can_access_admin: false,
    can_access_team_management: true,
    can_view_all_profiles: false, // Scoped to their district's schools
    can_manage_team: true,
    can_create_challenges: true,
    can_manage_organizations: false, // Schools are created by roster sync
    // DISTRICT_ROLE_SPEC §2: sees everything district-scoped, manages nothing
    // school-scoped. School admin power is the server-minted org_admin[] claim,
    // which a district admin may also hold — the two compose per user.
    can_manage_organization_members: false,
    can_set_organization_permissions: false,
  },
  super_admin: {
    can_access_analysis: true,
    can_access_challenges: true,
    can_access_feed: true,
    can_access_admin: true,
    can_access_team_management: true,
    can_view_all_profiles: true,
    can_manage_team: true,
    can_create_challenges: true,
    can_manage_organizations: true,
    can_manage_organization_members: true,
    can_set_organization_permissions: true,
  },
};

// Helper function to compute effective permissions for a user
export const computeUserPermissions = (
  userProfile: UserProfile,
  organizationPermissions: OrganizationPermission[] = []
): UserPermissions => {
  const basePermissions = ROLE_PERMISSIONS[userProfile.role];
  const enabledOrgPermissions = userProfile.enabled_permissions || [];

  // Intersect organization permissions with user's enabled permissions
  const activeOrgPermissions = organizationPermissions.filter(
    permission => enabledOrgPermissions.includes(permission)
  );

  return {
    base_permissions: basePermissions,
    organization_permissions: activeOrgPermissions,
    effective_permissions: {
      // Organization permissions are ADDITIVE to base permissions
      can_access_ai_analysis: basePermissions.can_access_analysis || activeOrgPermissions.includes('ai_analysis'),
      can_access_advanced_analytics: activeOrgPermissions.includes('advanced_analytics'),
      // Base permissions OR org permissions (additive)
      can_access_analysis: basePermissions.can_access_analysis || activeOrgPermissions.includes('ai_analysis'),
      can_access_challenges: basePermissions.can_access_challenges,
      can_access_feed: basePermissions.can_access_feed,
      can_access_admin: basePermissions.can_access_admin,
      can_access_team_management: basePermissions.can_access_team_management || activeOrgPermissions.includes('team_management'),
      can_view_all_profiles: basePermissions.can_view_all_profiles,
      can_manage_team: basePermissions.can_manage_team || activeOrgPermissions.includes('team_management'),
      can_create_challenges: basePermissions.can_create_challenges || activeOrgPermissions.includes('challenge_creation'),
      can_manage_organizations: basePermissions.can_manage_organizations,
      can_manage_organization_members: basePermissions.can_manage_organization_members,
      can_set_organization_permissions: basePermissions.can_set_organization_permissions,
    }
  };
};

// Default organization permissions for different plan types
export const DEFAULT_ORGANIZATION_PERMISSIONS: Record<string, OrganizationPermission[]> = {
  free: [], // Free plan - no permissions
  basic: [], // Basic plan does not include AI analysis
  premium: ['ai_analysis'], // Premium plan includes AI analysis
};
